/**
 * Unified Pin types — backs all six pinning use cases:
 *
 *   1. Stack pinned to top of Collection           (scope: collection / item: collection)
 *   2. Block pinned to Collection                  (scope: collection / item: block)
 *   3. Block pinned inside a Stack                 (scope: collection / item: block)
 *   4. Collection pinned inside a Group            (scope: collection / item: collection)
 *   5. Block pinned inside a Group                 (scope: collection / item: block)
 *   6. Collection pinned to top nav as a tab       (scope: account    / item: collection / surface: tab)
 *
 * Plus the existing account-level homepage feed pins (surface: feed) which
 * are migrated from `AccountPreferences.homepagePins` into the same Pin
 * table.
 *
 * Every pin is `(scope, surface, item, position)`, capped at
 * MAX_PINS_PER_SURFACE per (scope, surface). Uses the `type` discriminator
 * everywhere (per `.cursor/rules/discriminator-type.mdc`).
 */
import { z } from "zod";
// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------
/**
 * Max pins per (scope, surface) for account-scoped surfaces (feed, tab).
 * Enforced by PinEntity and backstopped by the DB CHECK constraint.
 */
export const MAX_PINS_PER_SURFACE = 5;
/**
 * Max pins per (scope, surface) for collection-scoped surfaces (default).
 * Higher than the account cap because pinned items within a collection are
 * organizational bookmarks, not homepage/nav-bar chrome.
 * Backstopped by the same DB CHECK constraint (position <= 9).
 */
export const MAX_COLLECTION_PINS_PER_SURFACE = 10;
/** Upper bound on pin item-id length. Wide enough for any prefixed CUID. */
export const MAX_PIN_ID_LEN = 128;
// ---------------------------------------------------------------------------
// Pin item — what is pinned
// ---------------------------------------------------------------------------
/**
 * What's being pinned. A Stack is just a Collection (layerIndex=2), so the
 * item type stays at two variants — the layer differentiation lives on the
 * Collection row, not the Pin.
 */
export const PinItemRefSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("block"),
        id: z.string().min(1).max(MAX_PIN_ID_LEN),
    }),
    z.object({
        type: z.literal("collection"),
        id: z.string().min(1).max(MAX_PIN_ID_LEN),
    }),
]);
/** Stable string key for dedup / membership checks. */
export function pinItemKey(item) {
    return `${item.type}:${item.id}`;
}
// ---------------------------------------------------------------------------
// Pin scope — where the pin lives
// ---------------------------------------------------------------------------
/**
 * Where the pin lives. `account` is implicit on the requesting user (no id
 * is sent over the wire; the entity layer reads `ctx.accountId`).
 * `collection` is any Collection row (Group/Collection/Stack — all layers).
 */
export const PinScopeSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("account") }),
    z.object({
        type: z.literal("collection"),
        id: z.string().min(1).max(MAX_PIN_ID_LEN),
    }),
]);
// ---------------------------------------------------------------------------
// Pin surface — which UI surface within a scope
// ---------------------------------------------------------------------------
/**
 * Account-scope surfaces. Both share the same MAX_PINS_PER_SURFACE (5) cap
 * but maintain independent lists:
 *   - `feed`: homepage feed pins (blocks + collections allowed)
 *   - `tab`:  persistent top-nav-bar tabs (collections only)
 */
export const AccountPinSurfaceSchema = z.enum(["feed", "tab"]);
/**
 * Collection-scope only has one surface today ("default" — the pinned
 * region rendered at the top of the Group/Collection/Stack view).
 * Modeled as an enum so future surfaces (e.g. "sidebar") slot in without
 * a schema change.
 */
export const CollectionPinSurfaceSchema = z.enum(["default"]);
/**
 * Union of every legal surface string across all scopes. Surface↔scope
 * compatibility is enforced by PinEntity, not by this schema (the
 * pairing rules are clearer in code than in a 2D-enum encoding).
 */
export const PinSurfaceSchema = z.enum(["feed", "tab", "default"]);
// ---------------------------------------------------------------------------
// Pin record (DB row shape, sent to clients on read)
// ---------------------------------------------------------------------------
export const PinScopeTypeSchema = z.enum(["account", "collection"]);
export const PinItemTypeSchema = z.enum(["block", "collection"]);
export const PinSchema = z.object({
    id: z.string(),
    scopeType: PinScopeTypeSchema,
    scopeId: z.string(),
    surface: PinSurfaceSchema,
    itemType: PinItemTypeSchema,
    itemId: z.string(),
    position: z.number().int().min(0).max(MAX_COLLECTION_PINS_PER_SURFACE - 1),
    pinnedBy: z.string(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime().optional(),
});
// ---------------------------------------------------------------------------
// Convenience: ordered list with the deduplication invariant
// ---------------------------------------------------------------------------
/**
 * List of pin item refs, up to MAX_COLLECTION_PINS_PER_SURFACE long (the
 * highest limit across all surfaces), no duplicate items. Used by the
 * wholesale-replace API (`POST /v1/pin/set`) and the homepage-pins
 * back-compat proxy. Per-surface entity caps are enforced at the entity layer.
 */
export const PinItemRefListSchema = z
    .array(PinItemRefSchema)
    .max(MAX_COLLECTION_PINS_PER_SURFACE)
    .superRefine((items, ctx) => {
    const seen = new Set();
    for (let i = 0; i < items.length; i++) {
        const key = pinItemKey(items[i]);
        if (seen.has(key)) {
            ctx.addIssue({ code: "custom", message: "Duplicate pin", path: [i] });
            return;
        }
        seen.add(key);
    }
});
